import { toast } from 'react-toastify';

import { getSerializedErrorMessage } from '../../../../../utils/errors';
import { ensureIntakeChildInfo } from './utils';

import type { ActionReducerMapBuilder, PayloadAction } from '@reduxjs/toolkit';
import type { IChildBackground, IChildInfo, IChildSchedule } from '../../../../../pages/common';
import type { IntakeQuestionnaireState } from '../../types';
import type {
  CreateIntakeChildBackgroundPayload,
  DeleteIntakeChildBackgroundPayload,
  DeleteIntakeChildSchedulePayload,
  IntakeChildThunks,
  UpdateIntakeChildInfoPayload,
} from './types';

export function addIntakeChildCases(
  builder: ActionReducerMapBuilder<IntakeQuestionnaireState>,
  thunks: IntakeChildThunks,
) {
  addUpdateIntakeChildInfoCases(builder, thunks);
  addCreateIntakeChildBackgroundCases(builder, thunks);
  addDeleteIntakeChildBackgroundCases(builder, thunks);
  addCreateIntakeChildScheduleCases(builder, thunks);
  addDeleteIntakeChildScheduleCases(builder, thunks);
}

function addUpdateIntakeChildInfoCases(
  builder: ActionReducerMapBuilder<IntakeQuestionnaireState>,
  thunks: IntakeChildThunks,
) {
  const { updateIntakeChildInfo } = thunks;

  builder
    .addCase(updateIntakeChildInfo.pending, (state) => {
      state.updateChildInfoLoading = true;
      state.error = null;
    })
    .addCase(
      updateIntakeChildInfo.fulfilled,
      (state, action: PayloadAction<IChildInfo, string, { arg: UpdateIntakeChildInfoPayload }>) => {
        state.updateChildInfoLoading = false;
        if (!state.intakeQuestionnaireDetails) return;
        // Merge only the fields this PUT changed so the separately-managed
        // backgrounds/schedules lists aren't clobbered by a response that omits them.
        const { child_info } = state.intakeQuestionnaireDetails;
        if (child_info) {
          Object.assign(child_info, action.meta.arg.data);
        } else {
          state.intakeQuestionnaireDetails.child_info = action.payload;
        }
      },
    )
    .addCase(updateIntakeChildInfo.rejected, (state, action) => {
      state.updateChildInfoLoading = false;
      state.error = action.error;
      toast.error(getSerializedErrorMessage(action.error));
    });
}

function addCreateIntakeChildBackgroundCases(
  builder: ActionReducerMapBuilder<IntakeQuestionnaireState>,
  thunks: IntakeChildThunks,
) {
  const { createIntakeChildBackground } = thunks;

  builder
    .addCase(createIntakeChildBackground.pending, (state) => {
      state.createChildBackgroundLoading = true;
      state.error = null;
    })
    .addCase(
      createIntakeChildBackground.fulfilled,
      (
        state,
        action: PayloadAction<
          IChildBackground,
          string,
          { arg: CreateIntakeChildBackgroundPayload }
        >,
      ) => {
        state.createChildBackgroundLoading = false;
        if (!state.intakeQuestionnaireDetails) return;
        const childInfo = ensureIntakeChildInfo(state.intakeQuestionnaireDetails);
        // Assign a new array so subscribers see a new `backgrounds` reference.
        // Take description/type from the request — the create response may omit them
        // (or use a different shape), and the list UI filters/displays those fields.
        childInfo.backgrounds = [
          ...childInfo.backgrounds,
          {
            ...action.payload,
            background_description: action.meta.arg.data.background_description,
            background_type: action.meta.arg.data.background_type,
          },
        ];
      },
    )
    .addCase(createIntakeChildBackground.rejected, (state, action) => {
      state.createChildBackgroundLoading = false;
      state.error = action.error;
      toast.error(getSerializedErrorMessage(action.error));
    });
}

function addDeleteIntakeChildBackgroundCases(
  builder: ActionReducerMapBuilder<IntakeQuestionnaireState>,
  thunks: IntakeChildThunks,
) {
  const { deleteIntakeChildBackground } = thunks;

  builder
    .addCase(deleteIntakeChildBackground.pending, (state) => {
      state.deleteChildBackgroundLoading = true;
      state.error = null;
    })
    .addCase(
      deleteIntakeChildBackground.fulfilled,
      (state, action: PayloadAction<void, string, { arg: DeleteIntakeChildBackgroundPayload }>) => {
        state.deleteChildBackgroundLoading = false;
        const { child_info } = state.intakeQuestionnaireDetails ?? {};
        if (!child_info) return;
        child_info.backgrounds = (child_info.backgrounds ?? []).filter(
          (row) => row.id !== action.meta.arg.backgroundId,
        );
      },
    )
    .addCase(deleteIntakeChildBackground.rejected, (state, action) => {
      state.deleteChildBackgroundLoading = false;
      state.error = action.error;
      toast.error(getSerializedErrorMessage(action.error));
    });
}

function addCreateIntakeChildScheduleCases(
  builder: ActionReducerMapBuilder<IntakeQuestionnaireState>,
  thunks: IntakeChildThunks,
) {
  const { createIntakeChildSchedule } = thunks;

  builder
    .addCase(createIntakeChildSchedule.pending, (state) => {
      state.createChildScheduleLoading = true;
      state.error = null;
    })
    .addCase(
      createIntakeChildSchedule.fulfilled,
      (state, action: PayloadAction<IChildSchedule>) => {
        state.createChildScheduleLoading = false;
        if (!state.intakeQuestionnaireDetails) return;
        const childInfo = ensureIntakeChildInfo(state.intakeQuestionnaireDetails);
        childInfo.schedule = [...childInfo.schedule, action.payload];
      },
    )
    .addCase(createIntakeChildSchedule.rejected, (state, action) => {
      state.createChildScheduleLoading = false;
      state.error = action.error;
      toast.error(getSerializedErrorMessage(action.error));
    });
}

function addDeleteIntakeChildScheduleCases(
  builder: ActionReducerMapBuilder<IntakeQuestionnaireState>,
  thunks: IntakeChildThunks,
) {
  const { deleteIntakeChildSchedule } = thunks;

  builder
    .addCase(deleteIntakeChildSchedule.pending, (state) => {
      state.deleteChildScheduleLoading = true;
      state.error = null;
    })
    .addCase(
      deleteIntakeChildSchedule.fulfilled,
      (state, action: PayloadAction<void, string, { arg: DeleteIntakeChildSchedulePayload }>) => {
        state.deleteChildScheduleLoading = false;
        const { child_info } = state.intakeQuestionnaireDetails ?? {};
        if (!child_info) return;
        child_info.schedule = (child_info.schedule ?? []).filter(
          (row) => row.id !== action.meta.arg.scheduleId,
        );
      },
    )
    .addCase(deleteIntakeChildSchedule.rejected, (state, action) => {
      state.deleteChildScheduleLoading = false;
      state.error = action.error;
      toast.error(getSerializedErrorMessage(action.error));
    });
}
